import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { PageHero } from "@/components/page-hero";
import { Download } from "lucide-react";
import { useState } from "react";
import { z } from "zod";
import { submitTemplateDownload } from "@/lib/submissions";
import { ConsentCheckbox, DocReviewCheckbox } from "@/components/consent-checkbox";
import { getTemplate, templateDownload } from "@/content/templates";
import { SeoFAQ } from "@/components/seo-faq";
import { AccreditationsStrip } from "@/components/accreditations-strip";
import { EabBannerBlock } from "@/components/eab-banner";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { ReviewStamp } from "@/components/review-stamp";
import { RelatedContent } from "@/components/related-content";
import { getRelatedContent } from "@/lib/related";
import { DEFAULT_AUTHOR } from "@/content/authors";
import { buildHead, faqSchema, ensureFAQs } from "@/lib/seo";
import { templateDepth } from "@/content/resource-depth";

const pkFor = (t: { slug: string; primaryKeyword?: string }) =>
  t.primaryKeyword ?? t.slug.replace(/-/g, " ");

export const Route = createFileRoute("/hr-templates/$slug")({
  loader: ({ params }) => {
    const template = getTemplate(params.slug);
    if (!template) throw notFound();
    return { template };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { template } = loaderData;
    const pk = pkFor(template);
    const path = `/hr-templates/${template.slug}`;
    // Breadcrumb JSON-LD is emitted once, by the visible trail in <PageHero>.
    const faqs = ensureFAQs(pk, templateDepth(template.slug)?.faqs ?? [], "template");
    return buildHead({
      title: template.metaTitle,
      description: template.metaDescription,
      path,
      schemas: [faqSchema(faqs)],
    });
  },
  component: TemplatePage,
});

function TemplatePage() {
  const { template } = Route.useLoaderData();
  const [unlocked, setUnlocked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    company_size: "1–10 employees",
  });
  const [consent, setConsent] = useState(false);
  const [docReview, setDocReview] = useState(false);
  const pk = pkFor(template);
  const depth = templateDepth(template.slug);
  const docNoun = /letter/i.test(template.title)
    ? "letter"
    : /policy/i.test(template.title)
      ? "policy"
      : /checklist|record|register/i.test(template.title)
        ? "record"
        : /contract/i.test(template.title)
          ? "contract"
          : "form";

  // Always serve the format the page copy promises (PDF, Word or Excel).
  const { url: downloadUrl, filename } = templateDownload(template);

  const triggerDownload = () => {
    const a = document.createElement("a");
    a.href = downloadUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const onDownload = async (e: React.FormEvent) => {
    e.preventDefault();
    const schema = z.object({
      name: z.string().trim().min(1, "Your name is required").max(100),
      email: z.string().trim().email("Enter a valid work email").max(255),
      company: z.string().trim().min(1, "Your company name is required").max(120),
      phone: z
        .string()
        .trim()
        .min(7, "Enter a valid phone number")
        .max(30)
        .regex(/^[0-9+()\-\s]+$/, "Enter a valid phone number"),
      company_size: z.string().max(40),
    });
    const parsed = schema.safeParse(form);
    if (!parsed.success) {
      setFormError(parsed.error.issues[0]?.message ?? "Please check the form.");
      return;
    }
    setSubmitting(true);
    setFormError(null);
    try {
      await submitTemplateDownload({
        ...parsed.data,
        template_slug: template.slug,
        consent,
        docReview,
      });
      setUnlocked(true);
      // Kick off the download immediately.
      setTimeout(triggerDownload, 150);
    } catch (err) {
      setFormError(
        err instanceof Error && err.message.startsWith("Please tick")
          ? err.message
          : "We couldn't record your request. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <PageHero
        breadcrumbs={[
          { label: "Home", path: "/" },
          { label: "Templates", path: "/hr-templates" },
          { label: template.title, path: `/hr-templates/${template.slug}` },
        ]}
        eyebrow={`Templates · ${template.format} · ${template.category}`}
        title={template.title}
        subtitle={`Free ${template.format} download for UK employers, written and reviewed by our HR team.`}
        intro={template.description}
        lastReviewed={template.lastReviewed}
        author={template.author ?? DEFAULT_AUTHOR}
      />


      <section className="px-6 py-16 lg:px-8">
        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-12 lg:grid-cols-12">
          <div className="lg:col-span-7">
            {depth?.covers?.length ? (
              <>
                <h2 className="font-serif text-2xl text-brand">What this {docNoun} covers</h2>
                <ul className="mt-4 space-y-2 text-foreground/90">
                  {depth.covers.map((c) => (
                    <li key={c} className="flex gap-3 leading-relaxed">
                      <span aria-hidden className="mt-2 size-1.5 shrink-0 rounded-full bg-accent" />
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            <h2 className={`${depth?.covers?.length ? "mt-10 " : ""}font-serif text-2xl text-brand`}>
              When to use this {docNoun}
            </h2>
            <p className="mt-4 leading-relaxed text-foreground/90">{template.whenToUse}</p>

            {depth?.process?.length ? (
              <>
                <h2 className="mt-10 font-serif text-2xl text-brand">{depth.processHeading}</h2>
                <ol className="mt-4 space-y-5">
                  {depth.process.map((s, i) => (
                    <li key={s.step} className="flex gap-4">
                      <span className="mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full bg-brand text-xs font-semibold text-brand-foreground">
                        {i + 1}
                      </span>
                      <div>
                        <p className="font-medium text-brand">{s.step}</p>
                        <p className="mt-1 leading-relaxed text-foreground/90">{s.detail}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </>
            ) : null}

            <h2 className="mt-10 font-serif text-2xl text-brand">How to complete it</h2>
            <p className="mt-4 leading-relaxed text-foreground/90">{template.instructions}</p>

            {depth?.mistakes?.length ? (
              <>
                <h2 className="mt-10 font-serif text-2xl text-brand">Common mistakes we see</h2>
                <ul className="mt-4 space-y-2 text-foreground/90">
                  {depth.mistakes.map((m) => (
                    <li key={m} className="flex gap-3 leading-relaxed">
                      <span aria-hidden className="mt-2 size-1.5 shrink-0 rounded-full bg-accent" />
                      <span>{m}</span>
                    </li>
                  ))}
                </ul>
              </>
            ) : null}

            {template.hubService && (
              <div className="mt-10 rounded-2xl border border-accent/30 bg-accent/5 p-6">
                <p className="text-xs font-semibold uppercase tracking-widest text-accent">Part of</p>
                <p className="mt-3 text-base leading-relaxed text-foreground">
                  This template is one letter inside a wider process. For end-to-end employer support, see{" "}
                  <Link
                    to="/services/$slug"
                    params={{ slug: template.hubService.slug }}
                    className="font-medium text-brand underline decoration-accent underline-offset-4 hover:text-accent"
                  >
                    {template.hubService.name}
                  </Link>
                  .
                </p>
              </div>
            )}

            <p className="mt-10 rounded-xl bg-card p-4 text-xs leading-relaxed text-muted-foreground ring-1 ring-border">
              This template is provided for general use and does not constitute legal advice. If your situation is
              complex, contact one of our HR experts before sending or signing.
            </p>
          </div>

          <aside className="lg:col-span-5">
            <div className="sticky top-24 rounded-2xl bg-brand p-6 text-brand-foreground">
              {unlocked ? (
                <>
                  <p className="font-serif text-xl">Your download has started.</p>
                  <p className="mt-2 text-sm text-brand-foreground/70">
                    If nothing happened, use the button below. We've also logged your request so we can email a copy once our sender domain is live.
                  </p>
                  <a
                    href={downloadUrl}
                    download={filename}
                    onClick={() => triggerDownload()}
                    className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-md bg-accent px-4 py-3 text-sm font-medium text-accent-foreground hover:brightness-110"
                  >
                    <Download className="size-4" /> Download {template.format}
                  </a>
                </>
              ) : (
                <>
                  <p className="font-serif text-xl">Get the template free</p>
                  <p className="mt-2 text-sm text-brand-foreground/70">
                    Drop your details and we'll download the {template.format} file straight away.
                  </p>
                  <form onSubmit={onDownload} noValidate className="mt-6 space-y-3">
                    <input
                      required
                      value={form.name}
                      onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                      maxLength={100}
                      placeholder="Your name"
                      className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm text-brand-foreground placeholder:text-brand-foreground/40 focus:outline-none focus:ring-1 focus:ring-accent"
                    />
                    <input
                      required
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                      maxLength={255}
                      placeholder="Work email"
                      className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm text-brand-foreground placeholder:text-brand-foreground/40 focus:outline-none focus:ring-1 focus:ring-accent"
                    />
                    <input
                      required
                      value={form.company}
                      onChange={(e) => setForm((f) => ({ ...f, company: e.target.value }))}
                      maxLength={120}
                      placeholder="Company name"
                      className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm text-brand-foreground placeholder:text-brand-foreground/40 focus:outline-none focus:ring-1 focus:ring-accent"
                    />
                    <input
                      required
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                      maxLength={30}
                      placeholder="Phone number"
                      className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm text-brand-foreground placeholder:text-brand-foreground/40 focus:outline-none focus:ring-1 focus:ring-accent"
                    />
                    <select
                      value={form.company_size}
                      onChange={(e) => setForm((f) => ({ ...f, company_size: e.target.value }))}
                      className="w-full rounded-md border border-white/15 bg-white/5 px-3 py-2 text-sm text-brand-foreground focus:outline-none"
                    >
                      <option className="text-foreground">1–10 employees</option>
                      <option className="text-foreground">11–50 employees</option>
                      <option className="text-foreground">51–250 employees</option>
                    </select>
                    {formError && <p className="text-xs text-accent">{formError}</p>}
                    <ConsentCheckbox id="consent-template-download" purpose="download" tone="dark" checked={consent} onChange={setConsent} />
                    <DocReviewCheckbox id="doc-review-template-download" tone="dark" checked={docReview} onChange={setDocReview} />
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-accent px-4 py-3 text-sm font-medium text-accent-foreground hover:brightness-110 disabled:opacity-60"
                    >
                      <Download className="size-4" />
                      {submitting ? "Sending…" : "Send me the template"}
                    </button>
                  </form>
                </>
              )}
              <p className="mt-4 text-[11px] text-brand-foreground/50">Last reviewed {template.lastReviewed}.</p>
            </div>

            <EabBannerBlock
              path={`/hr-templates/${template.slug}`}
              source="template_page"
              className="mt-6 !px-0 !pb-0"
            />
          </aside>
        </div>
      </section>


      <AccreditationsStrip />
      <SeoFAQ
        primaryKeyword={pk}
        faqs={depth?.faqs ?? []}
        type="template"
        heading={`Questions employers ask about this ${docNoun}`}
      />

      <RelatedContent
        heading="Related guides and tools"
        items={getRelatedContent({
          type: "template",
          slug: template.slug,
          primaryKeyword: pk,
          topics: [template.category],
          diversify: true,
        })}
      />
    </>
  );
}
